import React, { useState, useEffect, createContext, useContext } from 'react';
import {
  BrowserRouter as Router,
  useLocation,
  useNavigate
} from 'react-router-dom';
import { Box, Container } from '@mui/material';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import { FirebaseProvider } from './contexts/FirebaseContext.js';

// Custom contexts
import { ThemeProvider, useThemePreferences } from './contexts/ThemeContext.js';
import { UserProvider, useUser } from './contexts/UserContext.js';
import { RecipeProvider } from './contexts/recipe/RecipeContext.js';
import { InventoryProvider } from './contexts/InventoryContext.js';
import { MenuProvider } from './contexts/MenuContext.js';
import { CollectionProvider } from './contexts/CollectionContext.js';
import { ScrollProvider, useScroll } from './contexts/ScrollContext.js';

// Components
import TopBar from './components/TopBar.js';
import BottomNav from './components/BottomNav.js';
import AppRouter from './components/AppRouter.js';

// Navigation Context Definition
const NavigationContext = createContext();

function NavigationProvider({ children }) {
  const [navStack, setNavStack] = useState([]);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const currentPath = location.pathname;
    const from = location.state?.from || null;

    if (!location.key || location.key === 'default') {
      setNavStack([{ path: currentPath, from }]);
    } else {
      setNavStack(prev => {
        const lastEntry = prev[prev.length - 1];
        if (!lastEntry || lastEntry.path !== currentPath) {
          return [...prev, { path: currentPath, from }];
        }
        return prev;
      });
    }
  }, [location.pathname, location.state, location.key]);

  const getPreviousPage = (currentPath) => {
    const currentIndex = navStack.findIndex(entry => entry.path === currentPath);
    if (currentIndex > 0) {
      return navStack[currentIndex - 1];
    }
    return null;
  };

  const navigateWithState = (to, options = {}) => {
    navigate(to, options);
  };

  return (
    <NavigationContext.Provider value={{ navStack, getPreviousPage, navigateWithState }}>
      {children}
    </NavigationContext.Provider>
  );
}

export function useNavigation() {
  return useContext(NavigationContext);
}

function AppContent() {
  const { unitPreference } = useThemePreferences();
  const { scrollRef } = useScroll();

  // Function to reset search filters when navigating to home
  const resetSearchFilters = () => {
    localStorage.removeItem('recipeSearchTerms');
    localStorage.removeItem('recipeStatusFilters');
    localStorage.removeItem('recipeOriginFilters');
    localStorage.setItem('recipeSortOrder', 'asc');
  };

  return (
    <>
      <Helmet defaultTitle="Trove" />
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          minHeight: 'calc(var(--vh, 1vh) * 100)',
          backgroundColor: theme => theme.palette.background.default,
          color: theme => theme.palette.text.primary
        }}
      >
        <TopBar />
        <Box
          ref={scrollRef}
          sx={{
            flex: 1,
            overflowY: 'auto',
            overflowX: 'hidden',
            WebkitOverflowScrolling: 'touch',
            backgroundColor: theme => theme.palette.background.default,
            overscrollBehavior: 'contain',
            pb: theme => theme.breakpoints.down('sm') ? '64px' : 0,
            height: '100%',
            display: 'flex',
            flexDirection: 'column'
          }}
        >
          <Container
            maxWidth="md"
            sx={{
              py: { xs: 2, sm: 3 },
              px: { xs: 1.5, sm: 2 },
              pb: { xs: 8, sm: 3 },
              height: '100%',
              flex: 1,
              display: 'flex',
              flexDirection: 'column'
            }}
          >
            <AppRouter 
              scrollRef={scrollRef} 
              unitPreference={unitPreference}
              resetSearchFilters={resetSearchFilters} 
            />
          </Container>
        </Box>
        <BottomNav />
      </Box>
    </>
  );
}

// UserProviderWithChildren is a wrapper that provides the user to child contexts
const UserProviderWithChildren = ({ children }) => {
  const { user } = useUser();
  
  return (
    <RecipeProvider user={user}>
      <InventoryProvider user={user}>
        <MenuProvider user={user}>
          <CollectionProvider user={user}>
            {children}
          </CollectionProvider>
        </MenuProvider>
      </InventoryProvider>
    </RecipeProvider>
  );
};

function App() {
  return (
    <HelmetProvider>
      <ThemeProvider>
        <Router future={{
          v7_startTransition: true,
          v7_relativeSplatPath: true
        }}>
          <FirebaseProvider>
            <ScrollProvider>
              <NavigationProvider>
                <UserProvider>
                  <UserProviderWithChildren>
                    <AppContent />
                  </UserProviderWithChildren>
                </UserProvider>
              </NavigationProvider>
            </ScrollProvider>
          </FirebaseProvider>
        </Router>
      </ThemeProvider>
    </HelmetProvider>
  );
}

export default App;